import axios from 'axios';
import { addNotification } from 'state/actions';
import { NOTIFICATION_LEVELS } from 'constants/application';

export const BASE_URL = process.env.REACT_APP_API_URL;

/**
 * Cleans empty values from an object
 * @param {object} data The object to clean
 * @returns {object} The cleaned object
 */
export const cleanFormData = (data) => {
  if (data && typeof data === 'object') {
    const result = Object.keys(data).reduce((acc, key) => {
      if (data[key] !== '') {
        acc[key] = data[key];
      }
      return acc;
    }, {});

    return result;
  } else {
    return {};
  }
};

/**
 * Deserializes types contained within JSON response
 * @param {string} key
 * @param {string} value
 * @returns The the deserialized value
 */
export const reviver = (key, value) => {
  return value;
};

export const axiosInstance = axios.create({
  transformResponse: [
    (data) => {
      let resp = {};

      try {
        if (data) {
          resp = JSON.parse(data, reviver);
        }
      } catch (error) {
        throw Error(`[requestClient] Error parsing response JSON data - ${JSON.stringify(error)}`);
      }

      return resp;
    },
  ],
});

/**
 * A basic fetch wrapper to be used for all fetch requests.
 * @param {*} url The URL from which the resource will be requested
 * @param {*} options
 */
export const fetchResource = async (url, options = {}) => {
  const { method = 'GET' } = options;
  const request = buildRequest({
    ...options,
    isForm: method !== 'GET' ? true : false,
  });
  const { url: paramUrl, ...rest } = request;
  return axiosInstance(url, { ...rest, manual: true })
    .then(async ({ data = {}, status }) => {
      const { customError = false, errors = [] } = data;

      switch (status) {
        case 400:
          if (customError) {
            const message = errors.join(' ');
            // In this one case on the login screen, we do not want a toast message
            if (message !== 'Invalid username or password') {
              addNotification(errors.join(' '), NOTIFICATION_LEVELS.ERROR);
            }
          } else {
            addNotification(
              'An unexpected error has occurred. Please try again later.',
              NOTIFICATION_LEVELS.ERROR,
            );
          }
          break;
        default:
          // Everything else, including 500 errors
          // addNotification(
          //   ErrorMessages.UNEXPECTED_ERROR,
          //   NOTIFICATION_LEVELS.ERROR,
          // );
          break;
      }

      return data;
    })
    .catch(() => ({}));
};

export const buildRequest = (options = {}) => {
  const { method = 'GET', data = {}, headers = {}, isForm = false, ...rest } = options;
  return {
    method,
    headers: buildRequestHeaders(headers, isForm),
    data: isForm
      ? JSON.stringify({
          ...cleanFormData(data),
        })
      : data,
    mode: 'cors',
    ...rest,
    validateStatus: (statusCode) => {
      switch (statusCode) {
        case 400:
          // TODO: Bad request -- validation error messages
          return true;
        case 404:
          // TODO: Not found -- Redirect to the NotFound route
          // window.location.href = '/todo/not-found';
          return true;
        default:
          // TODO: Everything else, including 500 errors... for now.
          return true;
      }
    },
  };
};

export const buildRequestHeaders = (customHeaders = {}, isForm = false) => ({
  ...(isForm && { Accept: 'application/json' }),
  ...(isForm && { 'Content-Type': 'application/json' }),
  ...customHeaders,
});

export const isResponseError = (response) =>
  response.errors === true ||
  (!!response.errors && Object.keys(response.errors).length > 0) ||
  response.customError === true;
